import { useEffect, useMemo, useState } from "react";
import {
  Avatar,
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useQuery } from "react-query";
import { getConstants } from "@utilifiles/methods/storage/storage";
import { useMessenger } from "./MessengerContext";
import { ClinicChatUser, getClinicUsersForChat } from "./services";

type Props = {
  open: boolean;
  onClose: () => void;
};

export default function NewChatDialog({ open, onClose }: Props) {
  const { userId } = getConstants();
  const currentUserId = Number(userId || 0);
  const { startChatWithUser } = useMessenger();
  const [searchText, setSearchText] = useState("");
  const [startingId, setStartingId] = useState<number | null>(null);

  useEffect(() => {
    if (!open) {
      setSearchText("");
      setStartingId(null);
    }
  }, [open]);

  const { data, isLoading, isFetching } = useQuery(
    ["messengerClinicUsers"],
    getClinicUsersForChat,
    {
      enabled: open,
      refetchOnWindowFocus: false,
    },
  );

  const users = useMemo(() => {
    const items = data?.response || [];
    const q = searchText.trim().toLowerCase();
    return items.filter((u: ClinicChatUser) => {
      if (u.id === currentUserId) return false;
      if (u.status != null && u.status !== 1) return false;
      if (!q) return true;
      return (u.name || "").toLowerCase().includes(q);
    });
  }, [data, currentUserId, searchText]);

  const handleSelect = async (user: ClinicChatUser) => {
    setStartingId(user.id);
    try {
      const conv = await startChatWithUser(user.id);
      if (conv) onClose();
    } finally {
      setStartingId(null);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle className="flex items-center justify-between pr-2">
        <Typography variant="h6" component="span">
          New message
        </Typography>
        <IconButton onClick={onClose} size="small" aria-label="Close">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <TextField
          fullWidth
          size="small"
          placeholder="Search clinic users…"
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ mb: 1.5 }}
        />
        {(isLoading || isFetching) && users.length === 0 ? (
          <Box className="flex justify-center py-6">
            <CircularProgress size={28} />
          </Box>
        ) : users.length === 0 ? (
          <Typography
            variant="body2"
            color="text.secondary"
            className="text-center py-6"
          >
            No users found
          </Typography>
        ) : (
          <List disablePadding dense>
            {users.map((user) => (
              <ListItemButton
                key={user.id}
                disabled={startingId != null}
                onClick={() => void handleSelect(user)}
              >
                <ListItemAvatar>
                  <Avatar>{(user.name || "?").charAt(0).toUpperCase()}</Avatar>
                </ListItemAvatar>
                <ListItemText
                  primary={user.name}
                  secondary={user.userGroupName}
                />
                {startingId === user.id && <CircularProgress size={18} />}
              </ListItemButton>
            ))}
          </List>
        )}
      </DialogContent>
    </Dialog>
  );
}
